import { ROUTES } from "../routes/routes.constants";
import { transformModulesToMenu } from "../utils/menuTransformer";
export const MODULE_DESCRIPTORS = Object.freeze([
    {
        name: "Account Performance",
        basePath: ROUTES.accountPerformance.path,
        subPaths: [ROUTES.accountPerformanceReport.path],
    },
    {
        name: "Commission Tracker",
        basePath: ROUTES.commissionTracker.path,
        subPaths: [ROUTES.commissionTrackerReport.path],
    },
    {
        name: "Meta Ad Audits",
        basePath: ROUTES.metaAdAudit.path,
        subPaths: [ROUTES.metaAdAuditDetail.path.replace("/:reportId", "/")],
    },
    {
        name: "Roles & Permissions",
        basePath: ROUTES.rolesPermission.path,
        subPaths: [
            ROUTES.rolePermissionCreate.path,
            ROUTES.rolePermissionEdit.path.replace("/:id", "/"),
        ],
    },
    {
        name: "Outreach Tracker",
        basePath: ROUTES.outreachTracker.path,
        subPaths: [ROUTES.addOutreachTracker.path, "/outreach-tracker/", "/affiliate-tracker/client-approval"],
    },
    {
        name: "Create Report",
        basePath: ROUTES.createReport.path,
        subPaths: [
            ROUTES.createReportAdd.path,
            ROUTES.createReportEdit.path.replace("/:reportId", "/"),
            ROUTES.createReportView.path.replace("/:reportId", "/"),
        ],
    },
]);
const matchesSubPath = (pathname, subPath) => subPath.endsWith("/")
    ? pathname.startsWith(subPath)
    : pathname === subPath || pathname.startsWith(subPath + "/");
/**
 * Resolve the owning module's base route for a pathname. Returns the base path
 * when the route is a module's main page OR one of its sub-pages; null when the
 * route isn't part of a multi-page module (caller then keeps its own behavior).
 */
export function resolveModuleBasePath(pathname) {
    for (const d of MODULE_DESCRIPTORS) {
        if (pathname === d.basePath)
            return d.basePath;
        if (d.subPaths.some((sp) => matchesSubPath(pathname, sp)))
            return d.basePath;
    }
    return null;
}
/**
 * Sidebar-ONLY sub-page → owning-module map.
 *
 * Unlike MODULE_DESCRIPTORS (which drives BOTH the sidebar highlight AND the header
 * page title via getModuleLabel), these entries affect ONLY the sidebar active state.
 * Use for shared drill-in pages that belong to a menu module for highlight purposes
 * but must keep their own header title.
 *
 * `/account-overview` is Affiliate Account Performance's drill-in, but the header must
 * still read "Account Overview" (and its tab-specific titles), so it lives here and NOT
 * in MODULE_DESCRIPTORS.
 */
const SIDEBAR_ONLY_MODULE_MAP = Object.freeze({
    [ROUTES.accountOverview.path]: ROUTES.affiliateAccountPerformance.path,
});
/**
 * Owning module base route for the SIDEBAR HIGHLIGHT. Same as resolveModuleBasePath,
 * plus SIDEBAR_ONLY_MODULE_MAP entries (which must never influence the header title).
 * Returns null when the route isn't part of any module. Do NOT use this for page
 * labels — use resolveModuleBasePath / getModuleLabel there.
 */
export function resolveSidebarModuleBasePath(pathname) {
    return resolveModuleBasePath(pathname) ?? SIDEBAR_ONLY_MODULE_MAP[pathname] ?? null;
}
const findLabelByTo = (items, to) => {
    for (const item of items) {
        if (item.to === to)
            return item.label;
        if (item.items && item.items.length > 0) {
            const found = findLabelByTo(item.items, to);
            if (found)
                return found;
        }
    }
    return null;
};
/**
 * Module name for a route's owning module: prefers the DB menu label, falling
 * back to the static descriptor name. Returns null when the route isn't a
 * mapped multi-page module, so the caller can use its normal page label.
 */
export function getModuleLabel(pathname, modules = []) {
    const basePath = resolveModuleBasePath(pathname);
    if (!basePath)
        return null;
    if (modules && modules.length > 0) {
        const dbLabel = findLabelByTo(transformModulesToMenu(modules), basePath);
        if (dbLabel)
            return dbLabel;
    }
    return MODULE_DESCRIPTORS.find((d) => d.basePath === basePath)?.name ?? null;
}
